/**
 * Privileged admin actions — free / Spark plan (NO Cloud Functions).
 *
 * On the free tier there are no callables. The admin is a real admin user
 * (there is an `admins/{uid}` doc for them), and the security rules let admins
 * UPDATE `users`. So every action here is a DIRECT `updateDoc` to the target
 * `users/{uid}` document:
 *   - approve  → { status: 'approved', approvedAt: Date.now() }
 *   - reject   → { status: 'rejected' }
 *   - block    → { status: 'blocked' }
 *   - unblock  → { status: 'approved' }
 *
 * Firestore realtime listeners reflect the resulting state, so the UI stays
 * "optimistic": the acting row is disabled while the write is in flight and the
 * listener drops/updates it on resolve. Push-campaign creation lives in
 * hooks/useCampaigns.ts (also a direct write, allowed for admins).
 */
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { collection, deleteDoc, doc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore';
import { COLLECTIONS, paths, type Admin, type PriceViewer, type User, type UserStatus } from '@mitang/shared';
import { auth, db } from '../lib/firebase';
import { useToast } from '../lib/toast';
import { errorMessage } from '../lib/errors';

/** Direct status update on a users/{uid} doc (admins may write per rules). */
async function setUserStatus(
  uid: string,
  fields: { status: UserStatus; approvedAt?: number },
): Promise<void> {
  await updateDoc(doc(db, paths.user(uid)), { ...fields });
}

export function useApproveUser() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; name?: string }>({
    mutationFn: async ({ uid }) => {
      await setUserStatus(uid, { status: 'approved', approvedAt: Date.now() });
    },
    onSuccess: (_data, vars) => {
      toast.success(`Approved ${vars.name ?? 'dealer'}.`);
    },
    onError: (err) => toast.error(`Approve failed: ${errorMessage(err)}`),
  });
}

export function useRejectUser() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; name?: string }>({
    // `reason` intentionally not persisted: the shared User type has no such
    // field and we must not fork it. A plain status change is enough.
    mutationFn: async ({ uid }) => {
      await setUserStatus(uid, { status: 'rejected' });
    },
    onSuccess: (_data, vars) => {
      toast.info(`Rejected ${vars.name ?? 'dealer'}.`);
    },
    onError: (err) => toast.error(`Reject failed: ${errorMessage(err)}`),
  });
}

export function useBlockUser() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; name?: string }>({
    mutationFn: async ({ uid }) => {
      await setUserStatus(uid, { status: 'blocked' });
    },
    onSuccess: (_data, vars) => toast.success(`Blocked ${vars.name ?? 'user'}.`),
    onError: (err) => toast.error(`Block failed: ${errorMessage(err)}`),
  });
}

export function useUnblockUser() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; name?: string }>({
    mutationFn: async ({ uid }) => {
      await setUserStatus(uid, { status: 'approved' });
    },
    onSuccess: (_data, vars) => toast.success(`Unblocked ${vars.name ?? 'user'}.`),
    onError: (err) => toast.error(`Unblock failed: ${errorMessage(err)}`),
  });
}

/** Editable profile fields on a user doc (admins may write per rules). */
export type EditableUserFields = Pick<
  User,
  'name' | 'mobile' | 'email' | 'companyName' | 'city' | 'gstNumber'
>;

export function useEditUser() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; fields: EditableUserFields; name?: string }>({
    mutationFn: async ({ uid, fields }) => {
      // Strip undefined so we never write `undefined` (Firestore rejects it);
      // gstNumber is optional.
      const patch: Record<string, unknown> = {
        name: fields.name,
        mobile: fields.mobile,
        email: fields.email,
        companyName: fields.companyName,
        city: fields.city,
      };
      if (fields.gstNumber && fields.gstNumber.trim()) {
        patch.gstNumber = fields.gstNumber.trim();
      }
      await updateDoc(doc(db, paths.user(uid)), patch);
    },
    onSuccess: (_data, vars) => toast.success(`Saved changes to ${vars.name ?? 'user'}.`),
    onError: (err) => toast.error(`Save failed: ${errorMessage(err)}`),
  });
}


/**
 * DELETE a user completely: their sign-in account AND their profile/presence
 * docs. Runs through the sync worker (only it can delete auth accounts),
 * authorized by the calling admin's ID token. Deleting does NOT prevent
 * re-signup — use Block for that.
 */
export function useDeleteUser() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; label?: string }>({
    mutationFn: async ({ uid }) => {
      const endpoint: string = import.meta.env.VITE_SYNC_ENDPOINT ?? '';
      if (!endpoint) throw new Error('VITE_SYNC_ENDPOINT is not configured.');
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ action: 'delete_user', idToken, uid }),
      });
      const data = (await res.json()) as { ok: boolean; message?: string };
      if (!data.ok) throw new Error(data.message ?? 'Delete failed');
    },
    onSuccess: (_data, vars) => toast.success(`Deleted ${vars.label ?? 'user'}.`),
    onError: (err) => toast.error(`Delete failed: ${errorMessage(err)}`),
  });
}


/* -------------------------- price visibility ------------------------------ */

/**
 * PRICE ACCESS allow-list (`price_viewers/{uid}`). Only users with an entry can
 * read `product_prices` — enforced by the security rules, so this is real
 * access control, not a UI toggle. Admins may write the list.
 */
export function usePriceViewers(): Set<string> {
  const [uids, setUids] = useState<Set<string>>(new Set());
  useEffect(() => {
    const ref = collection(db, COLLECTIONS.priceViewers);
    return onSnapshot(
      ref,
      (snap) => setUids(new Set(snap.docs.map((d) => d.id))),
      () => setUids(new Set()),
    );
  }, []);
  return uids;
}

export function useTogglePriceAccess() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; email?: string; grant: boolean; label?: string }>({
    mutationFn: async ({ uid, email, grant }) => {
      const ref = doc(db, paths.priceViewer(uid));
      if (grant) {
        const entry: PriceViewer = {
          uid,
          ...(email ? { email } : {}),
          grantedAt: Date.now(),
          ...(auth.currentUser?.uid ? { grantedBy: auth.currentUser.uid } : {}),
        };
        await setDoc(ref, entry);
      } else {
        await deleteDoc(ref);
      }
    },
    onSuccess: (_d, vars) =>
      toast.success(
        vars.grant
          ? `${vars.label ?? 'User'} can now see prices.`
          : `Price access removed for ${vars.label ?? 'user'}.`,
      ),
    onError: (err) => toast.error(`Price access change failed: ${errorMessage(err)}`),
  });
}


/* ----------------------------- admin management --------------------------- */

/** UIDs that currently have admin rights (an `admins/{uid}` doc). */
export function useAdminUids(): Set<string> {
  const [uids, setUids] = useState<Set<string>>(new Set());
  useEffect(() => {
    const ref = collection(db, COLLECTIONS.admins);
    return onSnapshot(
      ref,
      (snap) => setUids(new Set(snap.docs.map((d) => d.id))),
      () => setUids(new Set()),
    );
  }, []);
  return uids;
}

/**
 * Promote/demote an admin. Admin rights = an `admins/{uid}` doc (checked by the
 * security rules), so this is real authorization, not a UI flag. On demotion we
 * also clear the worker's edge cache so it takes effect immediately.
 */
export function useToggleAdmin() {
  const toast = useToast();
  return useMutation<void, unknown, { uid: string; email?: string; name?: string; grant: boolean }>({
    mutationFn: async ({ uid, email, name, grant }) => {
      const ref = doc(db, paths.admin(uid));
      if (grant) {
        const entry: Admin = {
          uid,
          ...(name ? { name } : {}),
          ...(email ? { email } : {}),
          createdAt: Date.now(),
        };
        await setDoc(ref, entry);
      } else {
        await deleteDoc(ref);
        const endpoint: string = import.meta.env.VITE_SYNC_ENDPOINT ?? '';
        if (endpoint) {
          const idToken = await auth.currentUser?.getIdToken();
          await fetch(endpoint, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ action: 'clear_admin_cache', idToken, uid }),
          }).catch(() => undefined);
        }
      }
    },
    onSuccess: (_d, vars) =>
      toast.success(
        vars.grant
          ? `${vars.email ?? 'User'} is now an admin.`
          : `Admin rights removed from ${vars.email ?? 'user'}.`,
      ),
    onError: (err) => toast.error(`Admin change failed: ${errorMessage(err)}`),
  });
}
